import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faNewspaper,
    faEnvelope,
    faRobot,
    faRss,
    faChartLine,
    faGlobe,
    faShieldAlt,
    faTag,
    faCheckCircle,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { TEXTS } from '@/constants/texts';

// ========================================
// LANDING PAGE - DOMAIN FOR SALE
// ========================================

const iconMap: Record<string, typeof faNewspaper> = {
    newspaper: faNewspaper,
    envelope: faEnvelope,
    robot: faRobot,
    rss: faRss,
    'chart-line': faChartLine,
    globe: faGlobe
};

export const LandingPage: React.FC = () => {
    const currentYear = new Date().getFullYear();
    const { landing } = TEXTS;

    return (
        <>
            <Helmet>
                <title>{landing.title} - Premium Domain For Sale | {landing.price} {landing.currency}</title>
                <meta name="description" content={`${landing.title} is for sale at ${landing.price} ${landing.currency}. ${landing.subheadline}`} />
            </Helmet>

            <div className="landing-page">
                <div className="landing-page__container">
                    {/* Badge */}
                    <div className="landing-page__badge">
                        <FontAwesomeIcon icon={faTag} />
                        <span>{landing.badge}</span>
                    </div>

                    {/* Domain Name */}
                    <h1 className="landing-page__domain">
                        {landing.title}
                    </h1>

                    {/* Price */}
                    <div className="landing-page__price">
                        <span className="landing-page__price-amount">{landing.price}</span>
                        <span className="landing-page__price-currency">{landing.currency}</span>
                    </div>

                    {/* Headline */}
                    <h2 className="landing-page__headline">
                        {landing.headline}
                    </h2>

                    {/* Subheadline */}
                    <p className="landing-page__subheadline">
                        {landing.subheadline}
                    </p>

                    {/* CTA Buttons */}
                    <div className="landing-page__cta">
                        <a
                            href={landing.cta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="landing-page__cta-primary"
                        >
                            {landing.cta.primary}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </a>
                    </div>

                    {/* Secure Transaction Note */}
                    <p className="landing-page__secure">
                        <FontAwesomeIcon icon={faShieldAlt} />
                        {landing.secureTransaction}
                    </p>

                    {/* Features List */}
                    <div className="landing-page__features">
                        <h3 className="landing-page__features-title">Why This Domain?</h3>
                        <ul className="landing-page__features-list">
                            {landing.features.map((feature, index) => (
                                <li key={index} className="landing-page__feature-item">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Use Cases */}
                    <div className="landing-page__use-cases">
                        <h3 className="landing-page__use-cases-title">{landing.useCases.title}</h3>
                        <div className="landing-page__use-cases-grid">
                            {landing.useCases.items.map((item, index) => (
                                <div key={index} className="landing-page__use-case-card">
                                    <FontAwesomeIcon icon={iconMap[item.icon] || faGlobe} />
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Final CTA */}
                    <div className="landing-page__final-cta">
                        <p className="landing-page__footer-text">{landing.footer}</p>
                        <a
                            href={landing.cta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="landing-page__cta-secondary"
                        >
                            {landing.cta.secondary}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </a>
                    </div>

                    {/* Footer */}
                    <footer className="landing-page__footer">
                        <p className="landing-page__copyright">
                            © {currentYear} {landing.title}
                        </p>
                    </footer>
                </div>
            </div>
        </>
    );
};

LandingPage.displayName = 'LandingPage';

export default LandingPage;
